import { Box, useTheme } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { DragEvent, ForwardedRef, ReactNode, forwardRef, useContext, useImperativeHandle, useRef, useState } from "react";
import Dropzone, { DropzoneRef, Accept, FileRejection, DropEvent } from 'react-dropzone';

import { useTranslation } from "react-i18next";

export const ImageTypes: Accept = { 
  "image/bmp": [".bmp"],
  "image/jpeg": [".jpeg", ".jpg"],
  "image/png": [".png"]

}
/**
 * An external interface to be able to manually access data from a parent component.  To use this 
 * functionality, in the parent component create a `useRef` that is assigned to this component. 
 * Then use these functions to access the desired data.  For example, `myRef.current.open()`.
 */
export interface OpenButtonHandler {
  /**
   * Opens the system file browser.
   */
  open(): void;
}

interface FileDropZoneProps {
  /**
   * An Object of accepted MIME types.  By default any file type will be accepted.  
   * See {@link https://developer.mozilla.org/en-US/docs/Web/API/window/showOpenFilePicker#accept}.
   */
  accept?: Accept;
  /**
   * The child elements, if any, to display inside the drop zone.
   */
  children: ReactNode;
  /**
   * The maximum number of files accepted.  The value 0 can be used for no limitation on how many files are accepted.
   * @default 1
   */
  maxFiles?: number;
  /**
   * Callback for when the drop event occurs.
   * @param files The list of files dropped.
   */
  onDrop(files: Array<File>): void;
}

interface FileDropZoneColors {
  backgroundColor: string;
  borderColor: string;
}

enum DragState {
  Default,
  Invalid,
  Valid
}

const FileDropZone = forwardRef(function FileDropZone(props: FileDropZoneProps, ref: ForwardedRef<OpenButtonHandler>): React.JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const maxFiles = props.maxFiles || 1;

  const dropzoneRef = useRef<DropzoneRef | null>(null);

  const [dragState, setDragState] = useState<DragState>(DragState.Default);

  useImperativeHandle(ref, (): OpenButtonHandler => {
    return {
      open() {
        dropzoneRef.current?.open();
      }
    };
  }, []);

  function getStateColors(): FileDropZoneColors {
    switch (dragState) {
      case DragState.Invalid: {
        return {
          backgroundColor: theme.colors.error.lighter,
          borderColor: theme.colors.error.light
        };
      }
      case DragState.Valid: {
        return {
          backgroundColor: theme.colors.success.lighter,
          borderColor: theme.colors.success.light
        }
      }
      default: {
        return {
          backgroundColor: theme.colors.primary.lighter,
          borderColor: theme.colors.primary.light
        }
      }
    }
  }

  function handleDropAccepted(files: Array<File>, event: DropEvent): void {
    props.onDrop(files);
    setDragState(DragState.Default);
  }

  function handleDropRejected(fileRejections: Array<FileRejection>, event: DropEvent): void {
    if (fileRejections.length > maxFiles) {
      enqueueSnackbar(i18n.t("file-drop-zone.validation.error-max-files"), { variant: "error", persist: true });
    } else {
      enqueueSnackbar(i18n.t("file-drop-zone.validation.error-file-type"), { variant: "error", persist: true });
    }
    setDragState(DragState.Default);
  }

  function handleDragEnter(event: DragEvent): void {
    if (props.accept) {
      for (const mime of event.dataTransfer.items) {
        if (!props.accept[mime.type]) {
          setDragState(DragState.Invalid);
          return;
        }
      }
    }
    setDragState(DragState.Valid);
  }

  function handleDragLeave(): void {
    setDragState(DragState.Default);
  }

  const themeColors = getStateColors();

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        border: "2px dashed",
        borderRadius: 1,
        padding: 0,
        textAlign: "center",
        backgroundColor: themeColors.backgroundColor,
        borderColor: themeColors.borderColor
      }}>
      <Dropzone 
        ref={dropzoneRef}
        accept={props.accept}
        maxFiles={maxFiles}
        multiple={maxFiles > 1}
        noClick={true}
        noDragEventsBubbling={true}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDropAccepted={handleDropAccepted}
        onDropRejected={handleDropRejected}>
        {({getRootProps, getInputProps}) => (
          <div {...getRootProps()}>
          <input {...getInputProps()} />
            {props.children}
          </div>
        )}
      </Dropzone>
    </Box>
  );
});

export default FileDropZone;